import { warnInDev } from './warnInDev';

/**
 * The tenant a stored browser preference belongs to. Branch is deliberately NOT part of it:
 * the same table across branches should keep its columns, its view and its saved views.
 */
export interface PrefsScope {
  orgSlug: string;
  restaurantId: string;
}

/** The browser-preference families this package owns: column layout, active view, saved views. */
export type PrefsNamespace = 'crud.cols' | 'crud.view' | 'crud.views';

/**
 * The key a given tenant's preference for a resource is stored under.
 *
 * Scoped because the resource key alone is the same for every restaurant and every account on
 * a browser: on a shared till, one restaurant's hidden columns and saved views — whose names
 * and filter values are that tenant's data — used to follow the next person into another.
 */
export function prefsStorageKey(namespace: PrefsNamespace, resourceKey: string, scope: PrefsScope): string {
  return `${namespace}.${scope.orgSlug}.${scope.restaurantId}.${resourceKey}`;
}

/** The unscoped key this resource used before preferences were tenant-scoped. */
export function legacyPrefsStorageKey(namespace: PrefsNamespace, resourceKey: string): string {
  return `${namespace}.${resourceKey}`;
}

/**
 * Reads this tenant's stored value, adopting the legacy unscoped one the first time.
 *
 * The legacy key is DELETED once adopted: it is shared by every tenant and every account on
 * the browser, so leaving it in place keeps exactly the leak the scoping exists to close. The
 * consequence is deliberate — the first tenant to load a resource inherits the old preference,
 * the rest start from the config defaults.
 *
 * Never throws; a storage that refuses to cooperate degrades to "nothing stored".
 */
export function readPrefsValue(namespace: PrefsNamespace, resourceKey: string, scope: PrefsScope): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const scoped = localStorage.getItem(prefsStorageKey(namespace, resourceKey, scope));
    if (scoped !== null) {
      return scoped;
    }
    const legacyKey = legacyPrefsStorageKey(namespace, resourceKey);
    const legacy = localStorage.getItem(legacyKey);
    if (legacy === null) {
      return null;
    }
    // The adoption has to PERSIST, not just serve this one read: the legacy key is deleted
    // next, so without the scoped write every later read would fall back to config defaults.
    localStorage.setItem(prefsStorageKey(namespace, resourceKey, scope), legacy);
    localStorage.removeItem(legacyKey);
    return legacy;
  } catch (error) {
    warnInDev(`could not read the stored ${namespace} preference for "${resourceKey}": ${String(error)}`);
    return null;
  }
}

/** Persists this tenant's value. Never throws: a full or private-mode storage must not break a click. */
export function writePrefsValue(
  namespace: PrefsNamespace,
  resourceKey: string,
  scope: PrefsScope,
  value: string
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(prefsStorageKey(namespace, resourceKey, scope), value);
  } catch (error) {
    warnInDev(`could not persist the ${namespace} preference for "${resourceKey}": ${String(error)}`);
  }
}
